import React from "react";
import {
  Grid,
  Paper,
  Typography,
  Box,
  Card,
  CardContent,
  Button,
  CircularProgress,
  Snackbar,
  Alert,
} from "@mui/material";
import {
  Store,
  Work,
  Inventory,
  TrendingUp,
  PlayArrow,
  Sync,
} from "@mui/icons-material";
import { useQuery, useMutation } from "react-query";
import { api } from "../utils/api";
import { useNavigate } from "react-router-dom";

function StatCard({ title, value, icon, color = "primary" }) {
  return (
    <Card>
      <CardContent>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box>
            <Typography color="textSecondary" gutterBottom>
              {title}
            </Typography>
            <Typography variant="h4" component="div">
              {value}
            </Typography>
          </Box>
          <Box color={`${color}.main`}>{icon}</Box>
        </Box>
      </CardContent>
    </Card>
  );
}

function Dashboard() {
  const navigate = useNavigate();

  const { data: stats, isLoading: statsLoading } = useQuery(
    "stats",
    async () => {
      const response = await api.get("/api/stats");
      return response.data;
    },
    {
      refetchInterval: 30000, // Refresh every 30 seconds
    }
  );

  const { data: recentExecutions, isLoading: executionsLoading } = useQuery(
    "recentExecutions",
    async () => {
      const response = await api.get("/api/executions?page=1&page_size=5");
      return response.data;
    },
    {
      refetchInterval: 10000, // Refresh every 10 seconds
    }
  );

  const [syncSnackbar, setSyncSnackbar] = React.useState({
    open: false,
    message: "",
    severity: "info",
  });

  const handleCloseSyncSnackbar = () => {
    setSyncSnackbar({ ...syncSnackbar, open: false });
  };

  // Google Sheets sync mutation
  const syncSheetsMutation = useMutation(
    () => api.post("/api/sheets/sync"),
    {
      onSuccess: (response) => {
        console.log("Google Sheets sync started:", response.data);
        setSyncSnackbar({
          open: true,
          message: "Google Sheets sync started successfully!",
          severity: "success",
        });
      },
      onError: (error) => {
        console.error("Failed to sync sheets:", error);
        setSyncSnackbar({
          open: true,
          message: "Failed to start Google Sheets sync.",
          severity: "error",
        });
      }
    }
  );

  const handleSyncSheets = () => {
    syncSheetsMutation.mutate();
  };

  if (statsLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Dashboard
      </Typography>

      {/* Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total E-shops"
            value={stats?.total_eshops || 0}
            icon={<Store fontSize="large" />}
            color="primary"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Active Jobs"
            value={stats?.active_jobs || 0}
            icon={<Work fontSize="large" />}
            color="success"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Products"
            value={stats?.total_products || 0}
            icon={<Inventory fontSize="large" />}
            color="info"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Recent Executions"
            value={stats?.recent_executions || 0}
            icon={<TrendingUp fontSize="large" />}
            color="warning"
          />
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        {/* Quick Actions */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Quick Actions
            </Typography>
            <Box display="flex" flexDirection="column" gap={2}>
              <Button
                variant="contained"
                startIcon={<PlayArrow />}
                onClick={() => navigate("/jobs")}
                fullWidth
              >
                Manage Scraping Jobs
              </Button>
              <Button
                variant="contained"
                startIcon={<Store />}
                onClick={() => navigate("/eshops")}
                fullWidth
              >
                Configure E-shops
              </Button>
              <Button
                variant="contained"
                startIcon={<Sync />}
                onClick={handleSyncSheets}
                disabled={syncSheetsMutation.isLoading}
                fullWidth
              >
                {syncSheetsMutation.isLoading ? "Syncing..." : "Sync Google Sheets"}
              </Button>
            </Box>
          </Paper>
        </Grid>

        {/* Recent Executions */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Recent Job Executions
            </Typography>
            {executionsLoading ? (
              <CircularProgress size={24} />
            ) : (
              <Box>
                {recentExecutions?.items?.length > 0 ? (
                  recentExecutions.items.map((execution) => (
                    <Box
                      key={execution.id}
                      sx={{
                        p: 2,
                        mb: 1,
                        border: 1,
                        borderColor: "divider",
                        borderRadius: 1,
                      }}
                    >
                      <Typography variant="body2">
                        Job ID: {execution.job_id}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Status: {execution.status}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Products: {execution.products_found}
                      </Typography>
                    </Box>
                  ))
                ) : (
                  <Typography color="textSecondary">
                    No recent executions
                  </Typography>
                )}
                <Button
                  size="small"
                  onClick={() => navigate("/monitoring")}
                  sx={{ mt: 1 }}
                >
                  View All
                </Button>
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>
      {/* Sync Snackbar */}
      <Snackbar
        open={syncSnackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSyncSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSyncSnackbar}
          severity={syncSnackbar.severity}
          sx={{ width: "100%" }}
        >
          {syncSnackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default Dashboard;
